import { chooseUTMSource, trackEvent } from 'shared/helpers/Analytics';

export type InstantTransferRecoveryBannerState =
  | 'at_risk'
  | 'access_lost'
  | 'access_lost_multi'
  | 'settlement_failed';

export type InstantTransferRecoverySettleSource = 'Load List' | 'Load Details';

type OrderLinkBannerState = 'at_risk' | 'access_lost' | 'settlement_failed';

type SettleCTABannerState = 'at_risk' | 'access_lost' | 'access_lost_multi';

interface BannerEventProps {
  banner_state: InstantTransferRecoveryBannerState;
  order_id: string | null;
  orders_count: number | null;
}

interface SettleCTAClickedProps {
  banner_state: SettleCTABannerState;
  order_id: string | null;
  orders_count: number | null;
}

interface OrderLinkClickedProps {
  banner_state: OrderLinkBannerState;
  order_id: string;
}

interface ContactSupportClickedProps {
  order_id: string | null;
}

interface SettleDialogOpenedProps {
  source: InstantTransferRecoverySettleSource;
  order_id: string;
}

interface SettleConfirmedProps {
  source: InstantTransferRecoverySettleSource;
  order_id: string;
  bank_last4: string;
}

interface SettleDialogCanceledProps {
  source: InstantTransferRecoverySettleSource;
  order_id: string;
  checkbox_was_checked: boolean;
}

const VIEWED_SESSION_PREFIX = 'it-recovery-viewed:';

const viewedBannersWithoutStorage = new Set<string>();

function getViewedBannerKey(props: BannerEventProps): string {
  return `${props.banner_state}:${props.order_id ?? 'null'}:${props.orders_count ?? 'null'}`;
}

function claimFirstViewThisSession(bannerKey: string): boolean {
  try {
    if (typeof sessionStorage !== 'undefined') {
      const storageKey = `${VIEWED_SESSION_PREFIX}${bannerKey}`;
      if (sessionStorage.getItem(storageKey) != null) return false;
      sessionStorage.setItem(storageKey, '1');
      return true;
    }
  } catch {}
  if (viewedBannersWithoutStorage.has(bannerKey)) return false;
  viewedBannersWithoutStorage.add(bannerKey);
  return true;
}

export function trackInstantTransferRecoveryBannerViewed(
  props: BannerEventProps,
): void {
  if (!claimFirstViewThisSession(getViewedBannerKey(props))) return;
  trackEvent('Carrier Viewed IT Recovery Banner', {
    utm_source: chooseUTMSource(),
    utm_medium: 'Load List',
    banner_state: props.banner_state,
    order_id: props.order_id,
    orders_count: props.orders_count,
  });
}

export function trackInstantTransferRecoverySettleCTAClicked(
  props: SettleCTAClickedProps,
): void {
  trackEvent('Carrier Clicked Settle on IT Recovery Banner', {
    utm_source: chooseUTMSource(),
    utm_medium: 'Load List',
    banner_state: props.banner_state,
    order_id: props.order_id,
    orders_count: props.orders_count,
  });
}

export function trackInstantTransferRecoveryDismissed(
  props: BannerEventProps,
): void {
  trackEvent('Carrier Dismissed IT Recovery', {
    utm_source: chooseUTMSource(),
    utm_medium: 'Load List',
    banner_state: props.banner_state,
    order_id: props.order_id,
    orders_count: props.orders_count,
  });
}

export function trackInstantTransferRecoveryOrderLinkClicked(
  props: OrderLinkClickedProps,
): void {
  trackEvent('Carrier Clicked Order Link on IT Recovery Banner', {
    utm_source: chooseUTMSource(),
    utm_medium: 'Load List',
    banner_state: props.banner_state,
    order_id: props.order_id,
  });
}

export function trackInstantTransferRecoveryContactSupportClicked(
  props: ContactSupportClickedProps,
): void {
  trackEvent('Carrier Clicked Contact Support on IT Recovery Banner', {
    utm_source: chooseUTMSource(),
    utm_medium: 'Load List',
    banner_state: 'settlement_failed',
    order_id: props.order_id,
  });
}

export function trackInstantTransferRecoverySettleDialogOpened(
  props: SettleDialogOpenedProps,
): void {
  const trigger =
    props.source === 'Load List' ? 'banner_cta' : 'payment_status_block';
  trackEvent('Carrier Opened Settle IT Advance Dialog', {
    utm_source: chooseUTMSource(),
    utm_medium: props.source,
    order_id: props.order_id,
    trigger,
  });
}

export function trackInstantTransferRecoverySettleConfirmed(
  props: SettleConfirmedProps,
): void {
  trackEvent('Carrier Confirmed Settle IT Advance', {
    utm_source: chooseUTMSource(),
    utm_medium: props.source,
    order_id: props.order_id,
    bank_last4: props.bank_last4,
  });
}

export function trackInstantTransferRecoverySettleDialogCanceled(
  props: SettleDialogCanceledProps,
): void {
  trackEvent('Carrier Canceled Settle IT Advance Dialog', {
    utm_source: chooseUTMSource(),
    utm_medium: props.source,
    order_id: props.order_id,
    checkbox_was_checked: props.checkbox_was_checked,
  });
}

export function extractBankLastFourDigits(
  bankAccount: string | null | undefined,
): string {
  if (!bankAccount) return '';
  const digits = bankAccount.replace(/\D/g, '');
  return digits.slice(-4);
}
